import { CustomTooltip, NormalButton, TextInput } from '@/shared/ui/components';
import classNames from 'classnames';
import { t } from 'ttag';
import DetailCardSkeleton from './detailCardSkeleton/detailCardSkeleton';
import type { EventParametersProps } from './eventParameters.types';
import './eventParameters.scss';

/**
 * Empty default callback standing in for an event handler prop that was not supplied. Takes no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

const EventParameters = ({
  isLoadedData,
  eventDuration = null,
  maxHr = null,
  minHr = null,
  isShowViewFullEventChart,
  isViewFullEventAvailable,
  onChange,
  onClickViewFullEvent = NOOP,
  isNotEdit,
  errorObject,
  isNotEditHr = false,
  isDisabledUserEventParams = false,
  hasFullData = false,
  isShowViewFullEventButton = false,
  isLongEvent = false,
  isShowWarning = false,
  isAbortedStudy,
}: EventParametersProps) =>
  isLoadedData ? (
    isViewFullEventAvailable && (
      <div className="detail-card event-parameters">
        <div className="header-container">
          <span className="header-text">{t`Event Parameters`}</span>

          {isShowViewFullEventButton && (
            <NormalButton
              id="view-event-button"
              className={classNames('b--transparent', !hasFullData ? 'o-50' : '')}
              buttonName={isShowViewFullEventChart ? t`Basic View` : t`View Full Event`}
              disabled={isNotEdit || !hasFullData}
              onClick={onClickViewFullEvent}
            />
          )}
        </div>

        <div className="content-container">
          <div className="flex flex-wrap">
            <div className="inputs-container flex-1 pr3">
              <CustomTooltip
                title={
                  isAbortedStudy
                    ? t`The study has been aborted. You cannot perform any actions.`
                    : undefined
                }
              >
                <div id="event-duration-tooltip">
                  <TextInput
                    name="eventDuration"
                    label={t`Event duration (sec)`}
                    isNumberFormat
                    disabled={
                      isNotEdit || isNotEditHr || isDisabledUserEventParams || isAbortedStudy
                    }
                    value={eventDuration ?? undefined}
                    onChange={onChange}
                    isError={!!errorObject?.eventDuration}
                    errorMessage={errorObject?.eventDuration}
                  />
                </div>
              </CustomTooltip>

              <CustomTooltip
                title={
                  isAbortedStudy
                    ? t`The study has been aborted. You cannot perform any actions.`
                    : undefined
                }
              >
                <div id="min-hr-tooltip" style={{ marginRight: '16px', marginLeft: '16px' }}>
                  <TextInput
                    name="minHr"
                    label={t`Min HR`}
                    isNumberFormat
                    disabled={
                      isNotEdit || isNotEditHr || isDisabledUserEventParams || isAbortedStudy
                    }
                    value={minHr ?? undefined}
                    onChange={onChange}
                    isError={!!errorObject?.minHr}
                    errorMessage={errorObject?.minHr}
                    decimalScale={0}
                  />
                </div>
              </CustomTooltip>
              <CustomTooltip
                title={
                  isAbortedStudy
                    ? t`The study has been aborted. You cannot perform any actions.`
                    : undefined
                }
              >
                <div id="max-hr-tooltip">
                  <TextInput
                    name="maxHr"
                    label={t`Max HR`}
                    isNumberFormat
                    disabled={
                      isNotEdit || isNotEditHr || isDisabledUserEventParams || isAbortedStudy
                    }
                    value={maxHr ?? undefined}
                    onChange={onChange}
                    isError={!!errorObject?.maxHr}
                    errorMessage={errorObject?.maxHr}
                    decimalScale={0}
                  />
                </div>
              </CustomTooltip>
            </div>
          </div>

          {isShowWarning && isLongEvent && !hasFullData && (
            <div className="warning-text">
              {t`Full event data for this event is currently not available. Please note, any changes you made may be different when the full data becomes available.`}
            </div>
          )}
        </div>
      </div>
    )
  ) : (
    <DetailCardSkeleton />
  );

export default EventParameters;
